/**
 * Auth Hooks (Application)
 *
 * React Query hooks for authentication operations.
 * User-related hooks are in the user module.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { userKeys } from '@/modules/user/application/hooks';

import { AuthStatus } from '../../domain/types';
import { authRepository } from '../../infrastructure/repositories';
import { useAuthStore } from '../store';

import type { LoginRequest } from '../../domain/types/requests';

// ============================================================================
// Query Keys
// ============================================================================

export const authKeys = {
  all: ['auth'] as const,
  googleAuthUrl: () => [...authKeys.all, 'googleAuthUrl'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get Google OAuth URL and redirect
 */
export function useGoogleLogin() {
  const { setStatus, setError, setAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const getUrlMutation = useMutation({
    mutationFn: async () => {
      // Get the Google OAuth URL from backend
      const { url } = await authRepository.getGoogleAuthUrl();
      return url;
    },
    onMutate: () => {
      setStatus(AuthStatus.Loading);
    },
    onSuccess: (url) => {
      // Redirect to Google OAuth
      window.location.href = url;
    },
    onError: (error) => {
      setStatus(AuthStatus.Unauthenticated);
      setError(error instanceof Error ? error.message : 'Failed to get Google auth URL');
    },
  });

  const handleCallbackMutation = useMutation({
    mutationFn: async ({ authCode, scopes }: { authCode: string; scopes?: string }) => {
      return authRepository.googleLoginWithAuthCode(authCode, scopes);
    },
    onMutate: () => {
      setStatus(AuthStatus.Loading);
    },
    onSuccess: (data) => {
      // User data is already fetched and returned from the auth flow
      // Set user in store - no need to refetch
      setAuthenticated(data.user);
      // Set initial data for user query cache (prevents refetch)
      queryClient.setQueryData(userKeys.profile(), data.user);
    },
    onError: (error) => {
      setStatus(AuthStatus.Unauthenticated);
      setError(error instanceof Error ? error.message : 'Google login failed');
    },
  });

  return {
    initiateGoogleLogin: getUrlMutation.mutate,
    handleGoogleCallback: handleCallbackMutation.mutate,
    isLoading: getUrlMutation.isPending || handleCallbackMutation.isPending,
    error: getUrlMutation.error || handleCallbackMutation.error,
  };
}

/**
 * Hook for email/password login
 */
export function useLogin() {
  const { setStatus, setError, setAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: LoginRequest) => authRepository.login(request),
    onMutate: () => {
      setStatus(AuthStatus.Loading);
    },
    onSuccess: (data) => {
      // User data is already fetched and returned from the auth flow
      setAuthenticated(data.user);
      // Set initial data for user query cache (prevents refetch)
      queryClient.setQueryData(userKeys.profile(), data.user);
    },
    onError: (error) => {
      setStatus(AuthStatus.Unauthenticated);
      setError(error instanceof Error ? error.message : 'Login failed');
    },
  });
}

/**
 * Hook for logout
 */
export function useLogout() {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authRepository.logout(),
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
    },
    onError: () => {
      // Still clear local state even if API fails
      clearAuth();
      queryClient.clear();
    },
  });
}
